import { useLazyQuery } from '@apollo/client/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Flex } from '~/components/blocks';
import { FieldError, TextField } from '~/components/partials/PatientBlocks';
import { FormErrorMessage } from '~/constants/messages';
import { numberOrHyphenRegexp, postalCodeRegexp } from '~/constants/regexp';
import { SearchPostalCodesDocument, SearchPostalCodesQuery } from '~/graphql/generated/graphql';
import { toHalfNumberWithHyphen } from '~/utils/convert';

export type Address = {
  postalCode: string;
  prefecture: string;
  city: string;
  town: string;
};

type Props = {
  required?: boolean;
  disabled?: boolean;
  initialPostalCode?: string;
  onChange: (address: Address) => void;
  placeholder?: string;
  validationError?: string;
};

type SearchAddressCandidate = {
  postalCode: string;
  prefecture: string;
  city: string;
  town: string;
};

type SearchAddressCandidates = { [key: string]: SearchAddressCandidate };

const CandidateAddresses = styled('ul')();

const CandidateItem = styled('li')(({ theme }) =>
  css({
    cursor: 'pointer',
    float: 'left',
    marginRight: theme.space.m,
    fontSize: theme.fontSizes.xs,
    textDecoration: 'underline',
  }),
);

const SearchAddressWrap = styled('div', {
  shouldForwardProp,
})();

const formatAddress = (address: SearchAddressCandidate) => {
  return {
    prefecture: address.prefecture,
    city: address.city,
    town: address.town,
  };
};

export const SearchAddress = React.memo((props: Props) => {
  const { required = true, onChange, validationError } = props;
  const [postalCode, setPostalCode] = useState<string>('');
  const [candidates, setCandidates] = useState<SearchAddressCandidates>({});
  const [error, setError] = useState<string>();
  const latestRequestedPostalCodeRef = useRef<string | undefined>(undefined);

  const [search, searchResult] = useLazyQuery(SearchPostalCodesDocument, {
    fetchPolicy: 'network-only',
  });

  const handleSearchResult = useCallback(
    (inputPostalCode: string, nodes: SearchAddressCandidate[]) => {
      if (nodes.length === 0) {
        onChange({ postalCode: inputPostalCode, prefecture: '', city: '', town: '' });
        setError(FormErrorMessage.addressNotFound);
        return;
      }

      setError(undefined);

      if (nodes.length > 1) {
        const results: SearchAddressCandidates = {};
        nodes.forEach((node) => {
          const key = `${node.prefecture}${node.city}`;
          if (results[key] === undefined) {
            results[key] = node;
          }
        });
        if (Object.keys(results).length > 1) {
          setCandidates(results);
        } else {
          onChange({ postalCode: inputPostalCode, ...formatAddress(nodes[0]) });
        }
      } else {
        onChange({ postalCode: inputPostalCode, ...formatAddress(nodes[0]) });
      }
    },
    [onChange],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const _postalCode = e.target.value;

      if (required && !_postalCode) {
        setError(FormErrorMessage.requiredPostalCode);
      } else {
        setError(undefined);
      }

      setCandidates({});
      onChange({
        postalCode: _postalCode,
        prefecture: '',
        city: '',
        town: '',
      });
      setPostalCode(_postalCode);
    },
    [required, onChange],
  );

  useEffect(() => {
    setCandidates({});
    latestRequestedPostalCodeRef.current = undefined;
    setPostalCode(props.initialPostalCode || '');
    setError(undefined);
  }, [props.initialPostalCode]);

  useEffect(() => {
    if (!searchResult.error) return;
    setError(FormErrorMessage.addressNotFound);
  }, [searchResult.error]);

  useEffect(() => {
    if (!postalCode) {
      latestRequestedPostalCodeRef.current = undefined;
      return;
    }

    if (!numberOrHyphenRegexp.test(postalCode)) {
      setError(FormErrorMessage.number);
      latestRequestedPostalCodeRef.current = undefined;
      return;
    }

    const normalizedPostalCode = toHalfNumberWithHyphen(postalCode);

    if (!normalizedPostalCode.match(postalCodeRegexp)) {
      setError(FormErrorMessage.invalidPostalCode);
      latestRequestedPostalCodeRef.current = undefined;
      return;
    }

    if (latestRequestedPostalCodeRef.current === normalizedPostalCode) {
      return;
    }

    setError(undefined);
    latestRequestedPostalCodeRef.current = normalizedPostalCode;

    search({ variables: { postalCode: normalizedPostalCode } })
      .then((result: { data?: SearchPostalCodesQuery | null }) => {
        if (latestRequestedPostalCodeRef.current !== normalizedPostalCode) return;

        const nodes = result.data?.postalCodes?.nodes;
        if (nodes == null) {
          latestRequestedPostalCodeRef.current = undefined;
          return;
        }

        handleSearchResult(postalCode, nodes as SearchAddressCandidate[]);
      })
      .catch(() => {
        if (latestRequestedPostalCodeRef.current !== normalizedPostalCode) return;
        latestRequestedPostalCodeRef.current = undefined;
        setError(FormErrorMessage.addressNotFound);
      });
  }, [handleSearchResult, postalCode, search]);

  return (
    <SearchAddressWrap>
      <Flex>
        <TextField
          aria-label="住所検索"
          disabled={props.disabled}
          error={!!error}
          value={postalCode}
          onChange={handleChange}
          placeholder={props.placeholder}
          inputMode="numeric"
        />
      </Flex>
      {validationError ? (
        <FieldError error={validationError} />
      ) : error ? (
        <FieldError error={error} />
      ) : null}
      {!searchResult.loading && (
        <CandidateAddresses>
          {Object.entries(candidates).map((value) => {
            const [key, candidate] = value;
            return (
              <CandidateItem
                key={key}
                onClick={() => onChange({ postalCode, ...formatAddress(candidate) })}
              >
                {`${candidate.prefecture}${candidate.city}`}
              </CandidateItem>
            );
          })}
        </CandidateAddresses>
      )}
    </SearchAddressWrap>
  );
});

SearchAddress.displayName = 'SearchAddress';
